import { useEffect, useState } from 'react';
import useVideoContext from '../useVideoContext/useVideoContext';
import { RemoteDataTrackPublication, RemoteParticipant } from 'twilio-video';

/*
  Returns the participant that is sharing their datatrack (if any). This hook assumes that only one participant
  can share a datatrack at a time. Which for our purpose is ok. Since it's always a one-on-one conversation and we
  don't have to listen to our own datatrack events.
*/
export default function useRemoteDatatrack() {
  const { room } = useVideoContext();
  const [remoteDatatrack, setRemoteDatatrack] = useState<RemoteDataTrackPublication | null>(null);

  useEffect(() => {
    if (room?.state !== 'connected') return;

    const inspectParticipant = (participant: RemoteParticipant) => {
      participant.tracks.forEach(publication => {
        if (publication.kind === 'data') {
          setRemoteDatatrack(publication as RemoteDataTrackPublication);
        }
      });
    };

    room.participants.forEach(p => inspectParticipant(p as RemoteParticipant));

    // When a participant connects, inspect its publications
    const publishedHandlers = new Map<string, (publication: any) => void>();
    const unpublishedHandlers = new Map<string, (publication: any) => void>();

    const onParticipantConnected = (participant: RemoteParticipant) => {
      inspectParticipant(participant);
      const onPublished = (publication: any) => {
        if (publication.kind === 'data') {
          setRemoteDatatrack(publication as RemoteDataTrackPublication);
        }
      };
      const onUnpublished = (publication: any) => {
        if (publication.kind === 'data') {
          setRemoteDatatrack(null);
        }
      };
      publishedHandlers.set(participant.sid, onPublished);
      unpublishedHandlers.set(participant.sid, onUnpublished);
      participant.on('trackPublished', onPublished);
      participant.on('trackUnpublished', onUnpublished);
    };

    const onParticipantDisconnected = (participant: RemoteParticipant) => {
      setRemoteDatatrack(prev => (prev?.participant === participant ? null : prev) as any);
    };

    room.on('participantConnected', onParticipantConnected);
    room.on('participantDisconnected', onParticipantDisconnected);

    room.participants.forEach(p => onParticipantConnected(p as RemoteParticipant));

    return () => {
      room.off('participantConnected', onParticipantConnected);
      room.off('participantDisconnected', onParticipantDisconnected);
      room.participants.forEach(p => {
        const participant = p as RemoteParticipant;
        const pub = publishedHandlers.get(participant.sid);
        const unpub = unpublishedHandlers.get(participant.sid);
        if (pub) participant.off('trackPublished', pub);
        if (unpub) participant.off('trackUnpublished', unpub);
      });
    };
  }, [room]);

  return remoteDatatrack;
}
